/**
 * Landing on the dashboard (it is where `/` sends everyone but external
 * collaborators), its requests waited for the shell and then for the
 * dashboard's lazy code — on staging they started ~4.3s in, ~2.5s after the
 * app knew who the user was, and the page finished at ~6.8s. Here the code and
 * every request the dashboard opens with start as soon as the user has loaded.
 *
 * The arguments come from `store/query/dashboard/dashboardRequests`, the same
 * builders the widgets use, so each widget finds its request in RTK's cache
 * (or joins it in flight) instead of sending its own. Requests started here
 * are marked so the widgets use them as-is rather than refreshing them a
 * moment later.
 */
import { store } from '@/store';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { getAtsDashboardDetails } from '@/store/atsDashboard';
import { dashboardApi, dashboardNormalApi } from '@/store/query/dashboard';
import { markBootRequest } from '@/store/query/dashboard/readThrough';
import {
  activitiesArgs,
  activitiesMetricsArgs,
  applicationReviewArgs,
  openTasksArgs,
  upcomingInterviewsArgs,
  type ActivitiesState,
} from '@/store/query/dashboard/dashboardRequests';

interface CurrentUser {
  id?: string | number;
  role?: string;
  feature_options?: { ats_enabled?: boolean };
  requisition?: { enabled?: boolean };
}

interface DashboardState {
  activities: ActivitiesState;
  tasks: { sort?: { element?: string; order?: string } };
}

interface StartedRequest {
  requestId: string;
  unsubscribe: () => void;
  then: PromiseLike<unknown>['then'];
}

// Long enough for any boot; a session that never loads a user gives up.
const USER_WAIT_MS = 30_000;

let requested = false;

export const isDashboardPath = (pathname: string): boolean =>
  pathname === '/' || pathname === '/dashboard' || pathname === '/dashboard/';

const hasSession = (): boolean => {
  try {
    return Boolean(localStorage.getItem('apiv1Auth'));
  } catch {
    return false;
  }
};

/** Calls back once the current user is in the store (at once if it is). */
const whenUserLoaded = (callback: (user: CurrentUser) => void): void => {
  const current = selectCurrUser(store.getState()) as CurrentUser | undefined;
  if (current) {
    callback(current);
    return;
  }

  const unsubscribe = store.subscribe(() => {
    const user = selectCurrUser(store.getState()) as CurrentUser | undefined;
    if (!user) return;
    unsubscribe();
    clearTimeout(timer);
    callback(user);
  });
  // The listener only runs on a later dispatch, after `timer` is set.
  const timer = setTimeout(unsubscribe, USER_WAIT_MS);
};

/** Holds a request only until it settles; RTK keeps the result cached. */
const startRequest = (request: StartedRequest): void => {
  markBootRequest(request.requestId);
  request.then(
    () => request.unsubscribe(),
    () => request.unsubscribe()
  );
};

const startDashboardRequests = (user: CurrentUser): void => {
  const { activities, tasks } = getAtsDashboardDetails(
    store.getState()
  ) as DashboardState;
  const { dispatch } = store;
  const taskArgs = openTasksArgs(
    tasks?.sort,
    Boolean(user.requisition?.enabled)
  );

  [
    dispatch(
      dashboardApi.endpoints.getDashboardActivitiesMetrics.initiate(
        activitiesMetricsArgs(activities, user.id)
      )
    ),
    dispatch(
      dashboardApi.endpoints.getDashboardActivities.initiate(
        activitiesArgs(activities, user.id)
      )
    ),
    dispatch(
      dashboardApi.endpoints.getUpcomingInterviews.initiate(
        upcomingInterviewsArgs()
      )
    ),
    dispatch(
      dashboardApi.endpoints.getApplicationMetrics.initiate(
        applicationReviewArgs(1)
      )
    ),
    dispatch(
      dashboardNormalApi.endpoints.getScorecardTasks.initiate(
        taskArgs.scorecards
      )
    ),
    dispatch(
      dashboardNormalApi.endpoints.getSurveyTasks.initiate(taskArgs.surveys)
    ),
    dispatch(
      dashboardNormalApi.endpoints.getApprovalTasks.initiate(taskArgs.approvals)
    ),
  ].forEach((request) => startRequest(request as unknown as StartedRequest));
};

export const preloadDashboardRoute = (
  location: Pick<Location, 'pathname'> = window.location
): void => {
  if (requested || !isDashboardPath(location.pathname)) return;
  // Without a session the calls would only bounce off auth.
  if (!hasSession()) return;
  requested = true;

  whenUserLoaded((user) => {
    // `/` sends external collaborators to Projects, and accounts without the
    // ATS get the older dashboard: neither opens this one.
    if (user.role === 'external_collaborator') return;
    if (!user.feature_options?.ats_enabled) return;
    // Left the dashboard while the user was loading.
    if (!isDashboardPath(window.location.pathname)) return;

    // Same import as the `/dashboard` route in `components/getRoutes`, so both
    // resolve to one chunk.
    import('@/pages/dashboard-revamp').catch(() => {
      // A failed preload must not surface — React.lazy retries on navigation.
    });
    startDashboardRequests(user);
  });
};
